import { Alert, Typography, VisuallyHidden } from "@mantine/core";
import { useContext, useMemo, type PropsWithChildren } from "react";

import AppSettings from "src/AppSettings";
import Icon from "src/components/common/Icon";
import LayoutContext from "src/contexts/layouts/LayoutContext";
import useCurrentUserPermissions from "src/hooks/authentication/useCurrentUserPermissions";
import hasAnyPermission from "src/utilities/hasAnyPermission";

import InlineLoading from "../loading/InlineLoading";

/**
 * Presents page content, loading state and client permission messages.
 *
 * Postconditions: A falsy required permission bypasses denial. Undefined grants show No Access;
 * an empty or nonmatching grant list shows Permission Denied. Denied children remain mounted
 * inside VisuallyHidden, and permitted children remain mounted while the loading indicator shows.
 * Assumptions: Server authorization enforces access. permissionMode is a memo dependency but
 * does not change this helper's hasAnyPermission evaluation.
 */
const LayoutContent = ({ children }: PropsWithChildren) => {
    const permissions = useCurrentUserPermissions();
    const layoutContext = useContext(LayoutContext);

    const permissionStatus = useMemo(
        () => {
            if (!layoutContext.permission) {
                return { noAccess: false, permissionDenied: false };
            }

            if (!permissions) {
                return { noAccess: true, permissionDenied: false };
            }

            const permissionDenied =
                permissions.length === 0 ||
                !hasAnyPermission(
                    permissions,
                    [layoutContext.permission],
                    layoutContext.context,
                    layoutContext.permissionScopeMode
                );

            return { noAccess: false, permissionDenied };
        },
        [
            permissions,
            layoutContext.permission,
            layoutContext.permissionMode,
            layoutContext.permissionScopeMode,
            layoutContext.context
        ]
    );

    if (permissionStatus.noAccess) {
        return (
            <>
                <Alert
                    title="No Access"
                    icon={<Icon icon={AppSettings.icons.noAccess} />}
                    color="error"
                >
                    <Typography>
                        This user account has not been assigned any roles for this website.
                    </Typography>
                    <Typography>
                        If you believe that you should have access please contact TROG for help.
                    </Typography>
                </Alert>
                <VisuallyHidden>{children}</VisuallyHidden>
            </>
        );
    }

    if (permissionStatus.permissionDenied) {
        return (
            <>
                <Alert
                    color="warning"
                    title="Permission Denied"
                    icon={<Icon icon={AppSettings.icons.error.warning} />}
                >
                    You do not have permission to view this page.
                </Alert>
                <VisuallyHidden>{children}</VisuallyHidden>
            </>
        );
    }

    return (
        <>
            {layoutContext.loading && <InlineLoading height={200} />}
            {children}
        </>
    );
};

export default LayoutContent;
